import React, { useRef } from "react";
import { cn } from "./cn";
import { Button } from "./Button";

/* Paged navigation — the shared one. */

/* Which page numbers to show. Past a handful the run is elided with ellipses,
   keeping the control a fixed width instead of growing without limit — the
   first, the last, and a few either side of the current one. */
export const pageRange = (page, totalPages, window = 1) => {
  if (totalPages <= 5 + window * 2) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set([1, totalPages, page]);
  for (let i = 1; i <= window; i += 1) {
    if (page - i > 1) pages.add(page - i);
    if (page + i < totalPages) pages.add(page + i);
  }

  const sorted = [...pages].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return out;
};

/* The one place the summary is worded. */
export const paginationSummary = ({
  page = 1,
  pageSize = 0,
  total = 0,
  itemLabel = "result",
  itemLabelPlural,
}) => {
  const plural = itemLabelPlural || `${itemLabel}s`;
  if (!total) return `No ${plural}`;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return `Showing ${first}–${last} of ${total} ${total === 1 ? itemLabel : plural}`;
};

/* The line above the controls. Exported on its own so a screen that already
   has somewhere for a count can render it there without a second wording. */
export const PaginationSummary = ({ className = "", ...rest }) => (
  <p className={cn("text-[13px] font-semibold text-[var(--muted)] font-sans m-0", className)}>
    {paginationSummary(rest)}
  </p>
);

/* Where the page actually scrolls. */
export const scrollableAncestorOf = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle?.(node);
    const overflowY = style?.overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  /* Nothing nested scrolls: the document does. */
  return document.scrollingElement || document.documentElement || null;
};

/* Paging leaves you where you were standing, which on a full page of rows is
   the bottom of the previous page — the new rows are above the fold and the
   screen looks unchanged. Put the top of the new page in view. */
export const scrollToTopOf = (el) => {
  const target = scrollableAncestorOf(el);
  if (!target) return;

  /* Someone who has asked for less motion gets the jump, not the glide. */
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  const behavior = reduced ? "auto" : "smooth";

  if (typeof target.scrollTo === "function") {
    target.scrollTo({ top: 0, behavior });
    return;
  }
  /* jsdom, and very old browsers, have no scrollTo on elements. */
  target.scrollTop = 0;
};

const Chevron = ({ dir = "left" }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={dir === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} />
  </svg>
);

export const Pagination = ({
  page = 1,
  totalPages = 1,
  total,
  pageSize,
  itemLabel = "result",
  itemLabelPlural,
  onChange = () => {},
  className = "",
  label = "Pages",
  /* "numbered" is the default — Previous, the page numbers, Next. */
  variant = "numbered",
  /* Opt out where the list is short enough to sit on screen whole, or where
     the surrounding layout handles it. On by default: every paged list here
     is long enough to have scrolled by the time the pager is reachable. */
  scrollToTopOnChange = true,
}) => {
  const rootRef = useRef(null);
  const showSummary = typeof total === "number" && typeof pageSize === "number";

  /* One way in for all three controls, so Previous, Next and a number cannot
     drift apart. Scrolls only on an actual move — clicking the number you are
     already on should not throw the list about. */
  const go = (next) => {
    if (next === page) return;
    onChange(next);
    if (scrollToTopOnChange) scrollToTopOf(rootRef.current);
  };

  if (!showSummary && totalPages <= 1) return null;

  /* Below md the buttons share the row width and take a 44px touch target;
     from md they size to their content and sit at the two edges. */
  const step = "flex-1 min-h-[44px] md:flex-none md:min-h-0";

  return (
    <div ref={rootRef} className={cn("mt-5 flex flex-col gap-3", className)}>
      {showSummary && (
        <PaginationSummary
          page={page}
          pageSize={pageSize}
          total={total}
          itemLabel={itemLabel}
          itemLabelPlural={itemLabelPlural}
        />
      )}

      {totalPages > 1 && (
        <nav
          aria-label={label}
          className="flex flex-wrap items-center justify-center gap-2 md:justify-between md:gap-3"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => go(page - 1)}
            disabled={page === 1}
            className={step}
          >
            <Chevron dir="left" /> Previous
          </Button>

          {/* Numbers lead on narrow screens, where a full-width row of them
              reads better than a squeezed middle column. */}
          {variant === "compact" ? (
            /* aria-current marks it as the current page for the same reason
               the numbered variant does — it is the only thing here saying
               where you are. */
            <p
              aria-current="page"
              className="order-first m-0 w-full text-center text-[13.5px] font-semibold text-[var(--ink-2)] font-sans md:order-none md:w-auto"
            >
              Page {page} of {totalPages}
            </p>
          ) : (
            <div className="order-first flex w-full flex-wrap justify-center gap-1.5 md:order-none md:w-auto">
              {pageRange(page, totalPages).map((n, i) =>
                n === "…" ? (
                  <span
                    key={`gap-${i}`}
                    aria-hidden="true"
                    className="px-1 text-[13px] text-[var(--muted-2)] font-sans"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    onClick={() => go(n)}
                    aria-current={n === page ? "page" : undefined}
                    aria-label={`Page ${n}`}
                    className={cn(
                      "h-[34px] min-w-[34px] rounded-[9px] border px-2",
                      "text-[13px] font-semibold font-sans transition-colors",
                      n === page
                        ? "border-[var(--coral)] bg-[var(--orange-soft)] text-[var(--coral)]"
                        : "border-[var(--line)] bg-white text-[var(--ink-2)] hover:border-[var(--line-2)]"
                    )}
                  >
                    {n}
                  </button>
                )
              )}
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => go(page + 1)}
            disabled={page === totalPages}
            className={step}
          >
            Next <Chevron dir="right" />
          </Button>
        </nav>
      )}
    </div>
  );
};

export default Pagination;
